import {
  TextMatchCondition,
  TextMatchQuantifier,
  WbsItemSearchConditionV2VO,
} from '../../domain/value-object/WbsItemSearchConditionV2Vo'
import {
  FieldCondition,
  WbsItemSearchConditionV2Request,
} from '../model/wbsItemSearchConditionV2ApiRequest'
import { useCallback } from 'react'
import { dateTermVoService } from '../../domain/value-object/DateTermVO'
import { featureFlagService } from '../../utils/feature-flag'

type WbsItemSearchConditionV2ApiRequestTransformService = {
  toV2ApiRequest: (
    src: WbsItemSearchConditionV2VO,
    offset: number,
    limit: number
  ) => WbsItemSearchConditionV2Request
}

const buildTextMatchFieldConditions = (
  field: string,
  conditions: TextMatchCondition[] | undefined,
  includeQuantifier?: TextMatchQuantifier,
  excludeQuantifier?: TextMatchQuantifier
): FieldCondition[] => {
  if (!conditions || conditions.length === 0) return []
  const includeValues = conditions
    .filter(c => c.mode === 'include')
    .map(c => c.value)
  const excludeValues = conditions
    .filter(c => c.mode === 'exclude')
    .map(c => c.value)
  const result: FieldCondition[] = []
  if (includeValues.length > 0) {
    result.push({
      field,
      operator: includeQuantifier === 'all' ? 'CONTAINS_ALL' : 'LIKE',
      values: includeValues,
    })
  }
  if (excludeValues.length > 0) {
    result.push({
      field,
      operator: excludeQuantifier === 'all' ? 'NOT_CONTAINS_ALL' : 'NOT_LIKE',
      values: excludeValues,
    })
  }
  return result
}

const buildExactMatchFieldConditions = (
  field: string,
  conditions: TextMatchCondition[] | undefined,
  includeQuantifier?: TextMatchQuantifier,
  excludeQuantifier?: TextMatchQuantifier
): FieldCondition[] => {
  if (!conditions || conditions.length === 0) return []
  const includeValues = conditions
    .filter(c => c.mode === 'include')
    .map(c => c.value)
  const excludeValues = conditions
    .filter(c => c.mode === 'exclude')
    .map(c => c.value)
  const result: FieldCondition[] = []
  if (includeValues.length > 0) {
    result.push({
      field,
      operator: includeQuantifier === 'all' ? 'CONTAINS_ALL' : 'IN',
      values: includeValues,
    })
  }
  if (excludeValues.length > 0) {
    result.push({
      field,
      operator: excludeQuantifier === 'all' ? 'NOT_CONTAINS_ALL' : 'NOT_IN',
      values: excludeValues,
    })
  }
  return result
}

const buildFieldConditions = (
  src: WbsItemSearchConditionV2VO,
  useSprintsMultiSelect: boolean
): FieldCondition[] => {
  const conditions: FieldCondition[] = []

  conditions.push(
    ...buildExactMatchFieldConditions(
      'code',
      src.code,
      src.codeIncludeQuantifier,
      src.codeExcludeQuantifier
    )
  )
  conditions.push(
    ...buildTextMatchFieldConditions(
      'displayName',
      src.displayName,
      src.displayNameIncludeQuantifier,
      src.displayNameExcludeQuantifier
    )
  )
  conditions.push(
    ...buildTextMatchFieldConditions(
      'description',
      src.description,
      src.descriptionIncludeQuantifier,
      src.descriptionExcludeQuantifier
    )
  )
  if (src.types && src.types.length > 0) {
    conditions.push({ field: 'type', operator: 'IN', values: src.types })
  }
  if (src.status && src.status.length > 0) {
    conditions.push({ field: 'status', operator: 'IN', values: src.status })
  }
  if (src.ticketTypes && src.ticketTypes.length > 0) {
    conditions.push({
      field: 'ticketType',
      operator: 'IN',
      values: src.ticketTypes,
    })
  }
  if (src.priority && src.priority.length > 0) {
    conditions.push({
      field: 'priority',
      operator: 'IN',
      values: src.priority,
    })
  }
  if (src.teamUuid) {
    conditions.push({
      field: 'team.uuid',
      operator: 'EQ',
      values: [src.teamUuid],
    })
  }
  if (src.accountableUuid) {
    conditions.push({
      field: 'accountable.uuid',
      operator: 'EQ',
      values: [src.accountableUuid],
    })
  }
  if (src.responsibleUuid) {
    conditions.push({
      field: 'responsible.uuid',
      operator: 'EQ',
      values: [src.responsibleUuid],
    })
  }
  if (src.assigneeUuid) {
    conditions.push({
      field: 'assignee.uuid',
      operator: 'EQ',
      values: [src.assigneeUuid],
    })
  }
  if (src.deliverableUuid) {
    conditions.push({
      field: 'deliverable.uuid',
      operator: 'IN',
      values: [src.deliverableUuid],
    })
  }

  // Sprints
  if (useSprintsMultiSelect) {
    const sprintUuids = src.sprintUuids?.length
      ? src.sprintUuids
      : src.sprintUuid
        ? [src.sprintUuid]
        : undefined
    if (sprintUuids && sprintUuids.length > 0) {
      conditions.push({
        field: 'sprints.uuid',
        operator: 'IN',
        values: sprintUuids,
      })
    }
  } else if (src.sprintUuid) {
    conditions.push({
      field: 'sprints.uuid',
      operator: 'IN',
      values: [src.sprintUuid],
    })
  }

  // Tags
  if (src.tagUuids && src.tagUuids.length > 0) {
    const includeTagUuids = src.tagUuids
      .filter(c => c.mode === 'include')
      .map(c => c.value)
    const excludeTagUuids = src.tagUuids
      .filter(c => c.mode === 'exclude')
      .map(c => c.value)
    if (includeTagUuids.length > 0) {
      conditions.push({
        field: 'tags.uuid',
        operator: src.tagIncludeQuantifier === 'all' ? 'CONTAINS_ALL' : 'IN',
        values: includeTagUuids,
      })
    }
    if (excludeTagUuids.length > 0) {
      conditions.push({
        field: 'tags.uuid',
        operator:
          src.tagExcludeQuantifier === 'all' ? 'NOT_CONTAINS_ALL' : 'NOT_IN',
        values: excludeTagUuids,
      })
    }
  }

  // Date range conditions (blank takes priority over range)
  const dateFields = [
    'scheduledStartDate',
    'scheduledEndDate',
    'actualStartDate',
    'actualEndDate',
  ] as const
  for (const fieldName of dateFields) {
    if (src.blank?.[fieldName]) {
      conditions.push({
        field: fieldName,
        operator: 'IS_EMPTY',
        values: [],
      })
      continue
    }
    const dateTerm = src[fieldName]
    if (dateTerm) {
      const formatted = dateTermVoService.format(dateTerm)
      if (formatted.from) {
        conditions.push({
          field: fieldName,
          operator: 'GTE',
          values: [formatted.from],
        })
      }
      if (formatted.to) {
        conditions.push({
          field: fieldName,
          operator: 'LTE',
          values: [formatted.to],
        })
      }
    }
  }

  // DateTime conditions (createdAt, updatedAt)
  // DBはUTCで格納しているため、UTCに変換して送信する
  if (src.createdAt) {
    const formatted = src.createdAt.formatUtc()
    if (formatted.from) {
      conditions.push({
        field: 'createdAt',
        operator: 'GTE',
        values: [formatted.from],
      })
    }
    if (formatted.to) {
      conditions.push({
        field: 'createdAt',
        operator: 'LTE',
        values: [formatted.to],
      })
    }
  }
  if (src.updatedAt) {
    const formatted = src.updatedAt.formatUtc()
    if (formatted.from) {
      conditions.push({
        field: 'updatedAt',
        operator: 'GTE',
        values: [formatted.from],
      })
    }
    if (formatted.to) {
      conditions.push({
        field: 'updatedAt',
        operator: 'LTE',
        values: [formatted.to],
      })
    }
  }

  // Created/Updated by
  if (src.createdByUuid) {
    conditions.push({
      field: 'createdBy.uuid',
      operator: 'EQ',
      values: [src.createdByUuid],
    })
  }
  if (src.updatedByUuid) {
    conditions.push({
      field: 'updatedBy.uuid',
      operator: 'EQ',
      values: [src.updatedByUuid],
    })
  }

  return conditions
}

export const useWbsItemSearchConditionV2ApiRequestTransformService = (
  projectUuid: string
): WbsItemSearchConditionV2ApiRequestTransformService => {
  const useSprintsMultiSelect = featureFlagService.isEnabled(
    'sprints-multi-select'
  )

  const toV2ApiRequest = useCallback(
    (
      src: WbsItemSearchConditionV2VO,
      offset: number,
      limit: number
    ): WbsItemSearchConditionV2Request => ({
      projectUuid,
      rootWbsItemUuids: src.rootUuids?.length ? src.rootUuids : undefined,
      conditions: buildFieldConditions(src, useSprintsMultiSelect),
      offset,
      limit,
    }),
    [projectUuid, useSprintsMultiSelect]
  )

  return { toV2ApiRequest }
}
